/**
 * What fills the 18-30 seconds a try-on takes to render.
 *
 * Ported from the Shopify plugin's try-on flow, which had the same problem and
 * a good answer to it. Four things happen at once, and each one is doing a
 * distinct job:
 *
 *   The two photos — theirs and the garment's — drift toward each other on a
 *   loop. This is the only part that says *what* is being computed. A spinner
 *   is a machine thinking; two photos converging is legibly "these are being
 *   put together", which is exactly the operation in progress.
 *
 *   A status line reports the stage. Unlike the plugin's, it does not loop —
 *   it advances through the stages and holds on the last, because a status
 *   that cycles back to step one after twenty seconds says the work restarted.
 *
 *   A progress bar, paced to 18s and capped at 99%. Fake, and deliberately so:
 *   there is no progress signal to report. The partner API is one long call
 *   with no intermediate events (see services/mira/tryon_worker.py), so the
 *   honest alternative is no bar at all — which for a half-minute wait reads
 *   worse than a paced one. The cap is what keeps it from lying outright.
 *
 *   A fashion note, rotating every 4.5s. The actual anti-boredom device. The
 *   other three are about the render; this is the only part that is worth
 *   reading, and it is why the wait passes.
 *
 * Everything animated collapses under prefers-reduced-motion: the photos sit
 * still, the bar fills without transition, and the copy changes without
 * crossfading. The information is all still there — it is a wait indicator,
 * and someone who has asked for less motion still needs to know it is working.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Camera, Sparkles } from 'lucide-react';
import useReducedMotion from '../../hooks/useReducedMotion';
import { EXPECTED_SECONDS, STYLE_NOTES, TRYON_STAGES } from './tryOnWaitCopy';

const STAGE_MS = 4200;   // ~4 stages across an 18s render
const NOTE_MS = 4500;
const TICK_MS = 180;

/** Drifts inward and back on a loop. `side` is -1 for left, 1 for right. */
function MergeCard({ src, side, reduced, label }) {
  const rest = { x: side * 34, rotate: side * 6, scale: 0.97 };
  return (
    <motion.div
      className="absolute w-[68px] h-[92px] rounded-xl overflow-hidden bg-white ring-2 ring-white/95 shadow-[0_10px_30px_rgba(15,23,42,0.22)]"
      style={{ zIndex: side > 0 ? 2 : 1 }}
      initial={rest}
      animate={reduced ? rest : {
        x: [side * 34, side * 11, side * 34],
        rotate: [side * 6, side * 2, side * 6],
        scale: [0.97, 1.02, 0.97],
      }}
      transition={reduced ? { duration: 0 } : {
        duration: 2.6, ease: 'easeInOut', repeat: Infinity,
      }}
    >
      <img src={src} alt={label} className="w-full h-full object-cover" />
    </motion.div>
  );
}

export default function TryOnProgress({ userPhoto, garmentPhoto, garmentTitle = '', onChangePhoto }) {
  const reduced = useReducedMotion();
  const [stage, setStage] = useState(0);
  const [pct, setPct] = useState(0);
  // Randomised start so trying on three things in a row does not open with the
  // same note three times.
  const noteStart = useMemo(() => Math.floor(Math.random() * STYLE_NOTES.length), []);
  const [noteStep, setNoteStep] = useState(0);
  const startedAt = useRef(Date.now());

  // Advances and holds. Looping back to the first stage after twenty seconds
  // would read as the render having started over.
  useEffect(() => {
    const timer = setInterval(() => {
      setStage((s) => Math.min(s + 1, TRYON_STAGES.length - 1));
    }, STAGE_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNoteStep((n) => n + 1), NOTE_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const elapsed = (Date.now() - startedAt.current) / 1000;
      setPct(Math.min(99, (elapsed / EXPECTED_SECONDS) * 100));
    }, TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const note = STYLE_NOTES[(noteStart + noteStep) % STYLE_NOTES.length];
  const bothPhotos = Boolean(userPhoto && garmentPhoto);

  return (
    <div
      // `pointer-events-none` because nothing in here is interactive and the
      // things underneath it are. On the swipe card in particular, the drag
      // gesture lives on an ancestor — swallowing pointer events would mean a
      // card that cannot be swiped for the twenty seconds this is on screen.
      // `overflow-hidden` because this sits over a gallery whose image is
      // `h-auto` — a landscape product shot makes a frame shorter than this
      // content, and without it the notes spill out over the page below.
      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 px-6
                 bg-slate-900/50 backdrop-blur-[3px] pointer-events-none overflow-hidden"
      data-testid="tryon-progress"
      role="status"
      aria-live="polite"
    >
      {/* The one interactive thing on this overlay, so it opts back into
          pointer events on its own rather than the whole overlay reclaiming
          the swipe gesture underneath. Beta users couldn't find where to
          change the try-on photo, so this is a standing way in rather than
          a one-off hint. */}
      <button
        type="button"
        onClick={onChangePhoto}
        data-testid="tryon-progress-change-photo"
        className="absolute top-4 left-1/2 -translate-x-1/2 z-20 pointer-events-auto
                   inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                   bg-white/90 text-slate-900 text-[11px] font-semibold font-mahi-sans
                   shadow-sm active:scale-95 transition-transform"
      >
        <Camera className="w-3 h-3" />
        Change photo
      </button>

      {/* The photos, converging. Falls back to the spark alone when we don't
          have both — a single card drifting toward nothing is a worse story
          than no cards at all. */}
      <div className="relative w-[180px] h-[120px] flex items-center justify-center">
        <motion.div
          className="absolute w-[104px] h-[104px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(62,193,160,0.55), transparent 70%)' }}
          initial={{ opacity: 0.3, scale: 0.85 }}
          animate={reduced ? { opacity: 0.4, scale: 1 } : {
            opacity: [0.25, 0.85, 0.25], scale: [0.8, 1.08, 0.8],
          }}
          transition={reduced ? { duration: 0 } : { duration: 2.6, ease: 'easeInOut', repeat: Infinity }}
        />
        {bothPhotos && (
          <>
            <MergeCard src={userPhoto} side={-1} reduced={reduced} label="You" />
            <MergeCard src={garmentPhoto} side={1} reduced={reduced} label={garmentTitle} />
          </>
        )}
        <motion.div
          className="absolute z-[3] text-mahiteal-200 drop-shadow-[0_0_6px_rgba(62,193,160,0.8)]"
          initial={{ scale: 0.8, rotate: 0, opacity: 0.6 }}
          animate={reduced ? { scale: 1, opacity: 0.9 } : {
            scale: [0.7, 1.15, 0.7], rotate: [0, 90, 0], opacity: [0.55, 1, 0.55],
          }}
          transition={reduced ? { duration: 0 } : { duration: 2.6, ease: 'easeInOut', repeat: Infinity }}
        >
          <Sparkles className="w-5 h-5" />
        </motion.div>
      </div>

      {/* Status + progress. Both about the render itself. */}
      <div className="w-full max-w-[19rem] flex flex-col items-center gap-2.5">
        <div className="h-5 relative w-full flex items-center justify-center">
          <AnimatePresence mode="wait" initial={false}>
            <motion.p
              key={stage}
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 5 }}
              animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, y: -5 }}
              transition={{ duration: reduced ? 0.12 : 0.3 }}
              data-testid="tryon-progress-stage"
              className="absolute text-[13.5px] text-white font-mahi-sans text-center"
            >
              {TRYON_STAGES[stage]}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="w-full flex items-center gap-2.5">
          <div className="flex-1 h-[3px] rounded-full bg-white/25 overflow-hidden">
            <div
              className={`h-full rounded-full bg-mahiteal-300 ${reduced ? '' : 'transition-[width] duration-200 ease-linear'}`}
              style={{ width: `${pct}%` }}
              data-testid="tryon-progress-fill"
            />
          </div>
          <span
            className="text-[11px] font-semibold text-white/70 font-mahi-sans tabular-nums w-8 text-right"
            data-testid="tryon-progress-pct"
          >
            {Math.round(pct)}%
          </span>
        </div>
      </div>

      {/* The part that actually makes the wait pass. Fixed min-height so the
          layout above it doesn't jump every time a note of a different length
          swaps in — notes here run from one line to three. This was briefly
          `sm:min-h-*`, which is a viewport query and so did the opposite of
          what was wanted: stable on a desktop, jumping on the phone where the
          notes are actually read. */}
      <div className="w-full max-w-[19rem] min-h-[4.75rem] rounded-2xl bg-white/12 ring-1 ring-white/20 backdrop-blur-sm px-3.5 py-3 flex flex-col justify-center">
        <p className="text-[9.5px] tracking-[0.2em] uppercase text-white/55 font-semibold font-mahi-sans mb-1">
          While you wait
        </p>
        <AnimatePresence mode="wait" initial={false}>
          <motion.p
            key={note}
            initial={reduced ? { opacity: 0 } : { opacity: 0, y: 6 }}
            animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: -6 }}
            transition={{ duration: reduced ? 0.12 : 0.35 }}
            data-testid="tryon-progress-note"
            className="text-[12.5px] leading-relaxed text-white/90 font-mahi-sans"
          >
            {note}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}
